import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core'
import { WaypointFolderTree as WaypointFolderTreeType, CreateWaypointFolderDto } from '@/shared/api/waypointFolders'
import { WaypointResponse } from '@/shared/api/routes'
import { WaypointFolderItem } from './WaypointFolderItem'
import { WaypointTreeItem } from './WaypointTreeItem'
import { MoveWaypointToFolderDialog } from './MoveWaypointToFolderDialog'
import { useWaypointFolders } from '@/modules/routes/hooks/useWaypointFolders'
import { useAuth } from '@/modules/auth'
import { Button, Input, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/ui/components'
import { Folder, MapPin } from '@/ui/icons'
import { cn } from '@/shared/utils'
import { WaypointFoldersApi } from '@/shared/api/waypointFolders'

interface WaypointFolderTreeProps {
  waypoints: WaypointResponse[]
  selectedWaypoint?: WaypointResponse | null
  onWaypointClick?: (waypoint: WaypointResponse) => void
  onFolderClick?: (folderId: string) => void
  onWaypointMoved?: () => void
  onWaypointAddToTrip?: (waypoint: WaypointResponse) => void
  onWaypointDelete?: (waypoint: WaypointResponse) => void
  deletingWaypointId?: string | null
  onCreateFolderClick?: number | undefined
  // Selection mode props
  selectionMode?: boolean
  selectedWaypointIds?: Set<string>
  onToggleWaypointSelection?: (waypointId: string, checked: boolean) => void
  // Optional: Pass folders from parent to ensure they're in sync
  folders?: WaypointFolderTreeType[]
  // Callback when folder is created to refresh parent state
  onFolderCreated?: () => void
  // Optimistic update callbacks for instant UI updates
  onOptimisticCreateFolder?: (newFolder: WaypointFolderTreeType, parentId: string | null) => void
  onOptimisticUpdateFolder?: (folderId: string, updates: Partial<WaypointFolderTreeType>) => void
  onOptimisticDeleteFolder?: (folderId: string) => void
  /** When true, waypoints with folder_id null are rendered at root level (with folders). */
  showRootWaypoints?: boolean
}

interface FolderNode extends WaypointFolderTreeType {
  waypoints: WaypointResponse[]
}

// Recursively build folder tree with waypoints
// Only includes library waypoints (folder_id IS NOT NULL)
// Trip-specific waypoints (folder_id = null) are excluded from the library view
function buildFolderTreeWithWaypoints(
  folders: WaypointFolderTreeType[],
  waypoints: WaypointResponse[]
): FolderNode[] {
  // Filter out trip-specific waypoints - only show library waypoints (folder_id IS NOT NULL)
  const libraryWaypoints = waypoints.filter(wp => wp.folder_id !== null && wp.folder_id !== undefined)
  
  // Deduplicate waypoints by ID (keep first occurrence)
  const seenWaypointIds = new Set<string>()
  const uniqueLibraryWaypoints = libraryWaypoints.filter((wp) => {
    if (seenWaypointIds.has(wp.id)) {
      return false
    }
    seenWaypointIds.add(wp.id)
    return true
  })
  
  const waypointsByFolderId = new Map<string, WaypointResponse[]>()
  uniqueLibraryWaypoints.forEach((wp) => {
    const folderId = wp.folder_id || 'none'
    if (!waypointsByFolderId.has(folderId)) {
      waypointsByFolderId.set(folderId, [])
    }
    waypointsByFolderId.get(folderId)!.push(wp)
  })

  const buildNode = (folder: WaypointFolderTreeType): FolderNode => ({
    ...folder,
    waypoints: waypointsByFolderId.get(folder.id) || [],
    children: folder.children.map((child) => buildNode(child)),
  })

  return folders.map((folder) => buildNode(folder))
}

// Recursively find folder by ID
function findFolderById(
  folders: WaypointFolderTreeType[],
  id: string
): WaypointFolderTreeType | null {
  for (const folder of folders) {
    if (folder.id === id) {
      return folder
    }
    if (folder.children.length > 0) {
      const found = findFolderById(folder.children, id)
      if (found) return found
    }
  }
  return null
}

// Get folder depth
function getFolderDepth(folders: WaypointFolderTreeType[], folderId: string): number {
  const folder = findFolderById(folders, folderId)
  if (!folder) return 0
  return folder.depth || 1
}

export function WaypointFolderTree({
  waypoints,
  selectedWaypoint,
  onWaypointClick,
  onFolderClick,
  onWaypointMoved,
  onWaypointAddToTrip,
  onWaypointDelete,
  deletingWaypointId,
  onCreateFolderClick,
  selectionMode = false,
  selectedWaypointIds = new Set(),
  onToggleWaypointSelection,
  folders: foldersProp,
  onFolderCreated,
  onOptimisticCreateFolder,
  onOptimisticUpdateFolder,
  onOptimisticDeleteFolder,
  showRootWaypoints = false,
}: WaypointFolderTreeProps) {
  const { getAccessToken } = useAuth()
  const {
    folders: foldersFromHook,
    loading: foldersLoading,
    expandedFolders,
    toggleFolder,
    createFolder: createFolderFromHook,
    updateFolder,
    deleteFolder,
    moveWaypointToFolder,
  } = useWaypointFolders({ getAccessToken })
  
  // Use folders from prop if provided, otherwise use from hook
  const folders = foldersProp || foldersFromHook
  
  // Create API client for direct API calls when folders prop is provided
  const apiRef = React.useRef<WaypointFoldersApi | null>(null)
  React.useEffect(() => {
    apiRef.current = new WaypointFoldersApi(getAccessToken)
  }, [getAccessToken])
  
  // Wrapper for createFolder that skips hook's refresh when folders prop is provided
  // This prevents duplicate refreshes - parent will handle the refresh
  const createFolder = React.useCallback(
    async (data: CreateWaypointFolderDto): Promise<WaypointFolderTreeType | null> => {
      // If folders are provided as prop, call API directly to avoid child hook's refresh
      // Parent will handle the refresh via onFolderCreated callback
      if (foldersProp && apiRef.current) {
        try {
          const newFolder = await apiRef.current.createFolder(data)

          return {
            ...newFolder,
            children: newFolder.children || []
          } as WaypointFolderTreeType
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to create folder'

          throw err
        }
      }
      // Otherwise use hook's createFolder (which includes refresh)
      return await createFolderFromHook(data)
    },
    [foldersProp, createFolderFromHook]
  )

  const [activeId, setActiveId] = useState<string | null>(null)
  const [draggingOverFolder, setDraggingOverFolder] = useState<string | null>(null)
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false)
  const [newFolderParentId, setNewFolderParentId] = useState<string | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [waypointToMove, setWaypointToMove] = useState<WaypointResponse | null>(null)

  // Handle external create folder trigger (triggered by button click)
  useEffect(() => {
    if (onCreateFolderClick !== undefined && onCreateFolderClick > 0) {
      setShowCreateFolderDialog(true)
      setNewFolderParentId(null) // Create at root level
    }
  }, [onCreateFolderClick])

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    })
  )

  // Split root-level waypoints (folder_id null) vs library waypoints (folder_id set)
  const rootWaypoints = useMemo(
    () => (showRootWaypoints ? waypoints.filter((wp) => wp.folder_id == null) : []),
    [waypoints, showRootWaypoints]
  )
  const libraryWaypointsForTree = useMemo(
    () => waypoints.filter((wp) => wp.folder_id != null),
    [waypoints]
  )

  // Build folder tree with waypoints (library only)
  const folderTreeWithWaypoints = useMemo(
    () => buildFolderTreeWithWaypoints(folders, libraryWaypointsForTree),
    [folders, libraryWaypointsForTree]
  )

  // Handle drag start
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string)
  }, [])

  // Handle drag over (for visual feedback)
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event
    if (over) {
      const overId = over.id as string
      if (overId.startsWith('drop-folder-')) {
        const folderId = overId.replace('drop-folder-', '')
        setDraggingOverFolder(folderId)
      } else {
        setDraggingOverFolder(null)
      }
    } else {
      setDraggingOverFolder(null)
    }
  }, [])

  // Handle drag end
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveId(null)
      setDraggingOverFolder(null)

      const { active, over } = event
      if (!over) return

      const activeId = active.id as string
      const overId = over.id as string

      // Handle waypoint drag
      if (activeId.startsWith('waypoint-')) {
        const waypointId = activeId.replace('waypoint-', '')
        const waypoint = waypoints.find((wp) => wp.id === waypointId)
        if (!waypoint) return

        if (overId.startsWith('drop-folder-')) {
          const targetFolderId = overId.replace('drop-folder-', '')
          try {
            await moveWaypointToFolder(waypointId, targetFolderId)
            if (onWaypointMoved) {
              onWaypointMoved()
            }
          } catch (error) {
          }
        } else if (overId === 'root') {
          // Move waypoint to root (remove from folder)
          try {
            await moveWaypointToFolder(waypointId, null)
            if (onWaypointMoved) {
              onWaypointMoved()
            }
          } catch (error) {
          }
        }
      }

      // Handle folder drag (moving folder to new parent)
      if (activeId.startsWith('folder-')) {
        const folderId = activeId.replace('folder-', '')
        const draggedFolder = findFolderById(folders, folderId)
        if (!draggedFolder) return

        if (overId.startsWith('drop-folder-')) {
          const targetFolderId = overId.replace('drop-folder-', '')
          
          // Prevent moving folder into itself or its descendants
          if (targetFolderId === folderId) {

            return
          }

          // Check if target is a descendant
          const isDescendant = (checkId: string, ancestorId: string): boolean => {
            const checkFolder = findFolderById(folders, checkId)
            if (!checkFolder || !checkFolder.parent_id) return false
            if (checkFolder.parent_id === ancestorId) return true
            return isDescendant(checkFolder.parent_id, ancestorId)
          }

          if (isDescendant(targetFolderId, folderId)) {

            return
          }

          // Check depth limit
          const targetFolder = findFolderById(folders, targetFolderId)
          if (targetFolder) {
            const targetDepth = targetFolder.depth || 1
            if (targetDepth >= 3) {

              return
            }
          }

          try {
            await updateFolder(folderId, { parent_id: targetFolderId })
            // Auto-expand target folder
            if (!expandedFolders.has(targetFolderId)) {
              toggleFolder(targetFolderId)
            }
          } catch (error) {
          }
        } else if (overId === 'root') {
          // Move to root
          try {
            await updateFolder(folderId, { parent_id: null })
            // Refresh folders after move
            if (onWaypointMoved) {
              onWaypointMoved()
            }
          } catch (error) {
          }
        }
      }
    },
    [waypoints, folders, expandedFolders, moveWaypointToFolder, updateFolder, toggleFolder, onWaypointMoved]
  )

  // Handle move waypoint to folder
  const handleMoveToFolder = useCallback((waypoint: WaypointResponse) => {
    setWaypointToMove(waypoint)
  }, [])

  // Handle waypoint move completion
  const handleWaypointMove = useCallback(async (waypointId: string, folderId: string | null) => {
    try {
      await moveWaypointToFolder(waypointId, folderId)
      if (onWaypointMoved) {
        onWaypointMoved()
      }
      const folderName = folderId 
        ? findFolderById(folders, folderId)?.name || 'folder'
        : 'No Folder'

    } catch (error) {
      // Error already handled in moveWaypointToFolder
      throw error
    }
  }, [moveWaypointToFolder, onWaypointMoved, folders])

  // Handle create folder
  const handleCreateFolder = useCallback(async () => {
    if (!newFolderName.trim()) {

      return
    }

    try {
      setCreatingFolder(true)
      const data: CreateWaypointFolderDto = {
        name: newFolderName.trim(),
        parent_id: newFolderParentId,
      }
      
      // Optimistically create folder for instant UI update
      const tempId = `temp-${Date.now()}`
      let optimisticFolderId = tempId
      
      if (onOptimisticCreateFolder) {
        const optimisticFolder: WaypointFolderTreeType = {
          id: tempId, // Temporary ID
          user_id: '', // Will be set by server
          name: newFolderName.trim(),
          is_system: false,
          parent_id: newFolderParentId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          children: [],
          depth: newFolderParentId 
            ? (findFolderById(folders, newFolderParentId)?.depth || 1) + 1
            : 1,
        }
        onOptimisticCreateFolder(optimisticFolder, newFolderParentId)
        
        // Auto-expand parent folder if it exists
        if (newFolderParentId && !expandedFolders.has(newFolderParentId)) {
          toggleFolder(newFolderParentId)
        }
        // Auto-expand new folder
        toggleFolder(tempId)
        setShowCreateFolderDialog(false)
        setNewFolderName('')
        setNewFolderParentId(null)
      }
      
      // Create folder on server
      const newFolder = await createFolder(data)
      if (newFolder && onOptimisticUpdateFolder && optimisticFolderId) {
        // Replace temp folder with real folder data from server
        const realFolder: WaypointFolderTreeType = {
          ...newFolder,
          children: newFolder.children || [],
          depth: newFolderParentId 
            ? (findFolderById(folders, newFolderParentId)?.depth || 1) + 1
            : 1,
        }
        // Update optimistic folder with real ID and data from server
        onOptimisticUpdateFolder(optimisticFolderId, {
          id: realFolder.id,
          user_id: realFolder.user_id,
          created_at: realFolder.created_at,
          updated_at: realFolder.updated_at,
        })
        
        // Update expanded folders to use real ID
        if (expandedFolders.has(optimisticFolderId)) {
          toggleFolder(optimisticFolderId)
          toggleFolder(realFolder.id)
        }
        
        // Refresh in background to sync with server (for waypoint counts, etc.)
        if (onFolderCreated) {
          // Delay refresh slightly to allow optimistic update to render
          setTimeout(() => {
            onFolderCreated()
          }, 100)
        }
      }
    } catch (error) {
      // On error, revert optimistic update by refreshing
      if (onFolderCreated) {
        onFolderCreated()
      }
      // Error already handled in createFolder
    } finally {
      setCreatingFolder(false)
    }
  }, [
    newFolderName, 
    newFolderParentId, 
    createFolder, 
    expandedFolders, 
    toggleFolder, 
    onFolderCreated,
    onOptimisticCreateFolder,
    onOptimisticUpdateFolder,
    folders
  ])

  // Render folder node recursively
  const renderFolderNode = useCallback(
    (folder: FolderNode, depth: number = 0): React.ReactNode => {
      const isExpanded = expandedFolders.has(folder.id)
      const isDraggingOver = draggingOverFolder === folder.id

      return (
        <div key={folder.id} className="select-none">
          <WaypointFolderItem
            folder={folder}
            isExpanded={isExpanded}
            onToggle={() => toggleFolder(folder.id)}
            onSelect={() => {
              if (!expandedFolders.has(folder.id)) {
                toggleFolder(folder.id)
              }
              onFolderClick?.(folder.id)
            }}
            onDelete={async () => {
              // Optimistically delete folder for instant UI update
              if (onOptimisticDeleteFolder) {
                onOptimisticDeleteFolder(folder.id)
              }
              try {
                await deleteFolder(folder.id)
                // Refresh in background to sync with server
                if (onFolderCreated) {
                  setTimeout(() => {
                    onFolderCreated()
                  }, 100)
                }
              } catch (error) {
                // On error, revert by refreshing
                if (onFolderCreated) {
                  onFolderCreated()
                }
                throw error
              }
            }}
            onRename={async (newName: string) => {
              // Optimistically update folder name for instant UI update
              if (onOptimisticUpdateFolder) {
                onOptimisticUpdateFolder(folder.id, { name: newName })
              }
              try {
                await updateFolder(folder.id, { name: newName })
                // Refresh in background to sync with server
                if (onFolderCreated) {
                  setTimeout(() => {
                    onFolderCreated()
                  }, 100)
                }
              } catch (error) {
                // On error, revert by refreshing
                if (onFolderCreated) {
                  onFolderCreated()
                }
                throw error
              }
            }}
            onCreateSubfolder={() => {
              setNewFolderParentId(folder.id)
              setShowCreateFolderDialog(true)
            }}
            isDraggingOver={isDraggingOver}
            depth={depth}
            maxDepth={3}
          />

          {/* Children and waypoints */}
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-0.5">
              {/* Subfolders */}
              {folder.children.map((child) => renderFolderNode(child as FolderNode, depth + 1))}

              {/* Waypoints in this folder */}
              {folder.waypoints.map((waypoint) => (
                <WaypointTreeItem
                  key={waypoint.id}
                  waypoint={waypoint}
                  isSelected={selectedWaypoint?.id === waypoint.id}
                  onClick={onWaypointClick}
                  onAddToTrip={onWaypointAddToTrip}
                  onDelete={onWaypointDelete}
                  onMoveToFolder={handleMoveToFolder}
                  depth={depth + 1}
                  isDeleting={deletingWaypointId === waypoint.id}
                  selectionMode={selectionMode}
                  isChecked={selectedWaypointIds.has(waypoint.id)}
                  onToggleCheck={onToggleWaypointSelection}
                />
              ))}
            </div>
          )}
        </div>
      )
    },
    [
      expandedFolders,
      draggingOverFolder,
      toggleFolder,
      deleteFolder,
      updateFolder,
      onOptimisticDeleteFolder,
      onOptimisticUpdateFolder,
      onFolderCreated,
      selectedWaypoint,
      onWaypointClick,
      onFolderClick,
      onWaypointAddToTrip,
      onWaypointDelete,
      deletingWaypointId,
      handleMoveToFolder,
      selectionMode,
      selectedWaypointIds,
      onToggleWaypointSelection,
    ]
  )

  const activeDraggedItem = useMemo(() => {
    if (!activeId) return null
    if (activeId.startsWith('waypoint-')) {
      const waypointId = activeId.replace('waypoint-', '')
      return waypoints.find((wp) => wp.id === waypointId) || null
    }
    if (activeId.startsWith('folder-')) {
      const folderId = activeId.replace('folder-', '')
      return findFolderById(folders, folderId)
    }
    return null
  }, [activeId, waypoints, folders])

  if (foldersLoading) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">Loading folders...</p>
      </div>
    )
  }

  // Root drop zone component
  function RootDropZone() {
    const { setNodeRef, isOver } = useDroppable({
      id: 'root',
      data: {
        type: 'root',
        accepts: ['waypoint', 'folder'],
      },
    })

    return (
      <div
        ref={setNodeRef}
        className={cn(
          'min-h-[2px] rounded transition-colors',
          isOver && 'bg-adventure-200'
        )}
      />
    )
  }

  // Disable drag and drop in selection mode
  if (selectionMode) {
    return (
      <div className="space-y-1">
        {folderTreeWithWaypoints.map((folder) => renderFolderNode(folder, 0))}
      </div>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-1">
        {/* Root drop zone for moving items to root */}
        <RootDropZone />

        {/* Root-level waypoints (folder_id null) when showRootWaypoints is true */}
        {showRootWaypoints &&
          rootWaypoints.map((waypoint) => (
            <WaypointTreeItem
              key={waypoint.id}
              waypoint={waypoint}
              isSelected={selectedWaypoint?.id === waypoint.id}
              onClick={onWaypointClick}
              onAddToTrip={onWaypointAddToTrip}
              onDelete={onWaypointDelete}
              onMoveToFolder={handleMoveToFolder}
              depth={0}
              isDeleting={deletingWaypointId === waypoint.id}
              selectionMode={selectionMode}
              isChecked={selectedWaypointIds.has(waypoint.id)}
              onToggleCheck={onToggleWaypointSelection}
            />
          ))}

        {/* Render folder tree */}
        {folderTreeWithWaypoints.map((folder) => renderFolderNode(folder, 0))}
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activeDraggedItem && (
          <div className="bg-card text-card-foreground border border-border rounded-md shadow-lg p-2">
            {activeId?.startsWith('waypoint-') && 'name' in activeDraggedItem ? (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-adventure-600" />
                <span className="text-sm">
                  {(activeDraggedItem as WaypointResponse).name || 'Unnamed Waypoint'}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Folder className="w-4 h-4 text-adventure-600" />
                <span className="text-sm">
                  {(activeDraggedItem as WaypointFolderTreeType).name}
                </span>
              </div>
            )}
          </div>
        )}
      </DragOverlay>

      {/* Create Folder Dialog */}
      <Dialog open={showCreateFolderDialog} onOpenChange={setShowCreateFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Folder Name
              </label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter folder name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateFolder()
                  } else if (e.key === 'Escape') {
                    setShowCreateFolderDialog(false)
                  }
                }}
                autoFocus
              />
            </div>
            {newFolderParentId && (
              <div className="text-sm text-gray-500">
                Creating in: {findFolderById(folders, newFolderParentId)?.name || 'Unknown'}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateFolderDialog(false)
                setNewFolderName('')
                setNewFolderParentId(null)
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()}>
              {creatingFolder ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Waypoint to Folder Dialog */}
      <MoveWaypointToFolderDialog
        open={waypointToMove !== null}
        onOpenChange={(open) => {
          if (!open) {
            setWaypointToMove(null)
          }
        }}
        waypoint={waypointToMove}
        folders={folders}
        onMove={handleWaypointMove}
        onSuccess={() => {
          setWaypointToMove(null)
        }}
      />
    </DndContext>
  )
}

